// SPDX-License-Identifier: FSL-1.1-Apache-2.0
/**
 * @module useConsent
 * @package @geenius/analytics/react
 * @description Exposes provider-backed analytics consent state and
 * category-level helpers for the React variant.
 */

import type { ConsentSettings } from "@geenius/analytics";
import { AnalyticsContextError } from "@geenius/analytics";
import { useCallback } from "react";
import { useAnalyticsConsent } from "../providers/AnalyticsProvider";

export type ConsentScope =
  | "essential"
  | "analytics"
  | "marketing"
  | "personalization";

/**
 * Stable consent state and actions returned by {@link useConsent}.
 *
 * @property consentSettings Current category-level consent settings.
 * @property updateConsent Updates all consent settings and persists them to the tracker.
 * @property setConsent Alias for `updateConsent` for consumer ergonomics.
 * @property hasConsent Returns whether a specific category is currently granted.
 */
export interface UseConsentReturn {
  consentSettings: ConsentSettings | null;
  updateConsent: (settings: ConsentSettings) => void;
  setConsent: (settings: ConsentSettings) => void;
  hasConsent: (scope: ConsentScope) => boolean;
}

/**
 * Reads and updates analytics consent state from the nearest provider.
 *
 * @returns Consent state, write actions, and category-level helpers.
 * @throws {AnalyticsContextError} When called outside an `<AnalyticsProvider>`.
 */
export function useConsent(): UseConsentReturn {
  const context = useAnalyticsConsent();
  const setConsent = useCallback(
    (settings: ConsentSettings): void => {
      if (!context) {
        throw new AnalyticsContextError({
          message:
            "useConsent must be called inside an <AnalyticsProvider> tree.",
        });
      }

      context.updateConsent(settings);
    },
    [context],
  );
  const hasConsent = useCallback(
    (scope: ConsentScope): boolean =>
      context?.consentSettings?.[scope] === true,
    [context],
  );

  if (!context) {
    throw new AnalyticsContextError({
      message: "useConsent must be called inside an <AnalyticsProvider> tree.",
    });
  }

  return {
    consentSettings: context.consentSettings,
    updateConsent: setConsent,
    setConsent,
    hasConsent,
  };
}
